import ToggleNewData from './ToggleNewData';
import UpdateConditionals from './UpdateConditionals';
import CheckValidationsOnInput from './CheckValidationsOnInput';
import UpdateTotals from './UpdateTotals';
import UpdateProgressBar from './UpdateProgressBar';
import ToastCustom from 'src/javascripts/components/alerts/ToastCustom';

const TYPEAHEAD_STATE_CLASSES = 'no-asc no-desc yes-asc yes-desc high-asc high-desc low-asc low-desc medium information-not-available filled';

function clearTypeaheadStateClasses(el) {
  if (el && el.length !== 0) {
    el.removeClass(TYPEAHEAD_STATE_CLASSES);
  }
}

function resolveManualTypeahead(el) {
  if (!el || el.length === 0) { return el; }
  if (el.is('[data-list-options]')) { return el; }
  const wrapper = el.closest('.twitter-typeahead');
  if (wrapper.length !== 0) {
    const fallback = wrapper.find('input.manual-list-typeahead[data-list-options]').first();
    if (fallback.length !== 0) { return fallback; }
  }
  return el;
}

const MENU_PREFERRED_HEIGHT = 300;

function floatTypeaheadMenu(el) {
  const wrapper = el.closest('.twitter-typeahead');
  const tableContainer = el.closest('.table-container');
  // Once floated, the menu is a direct child of `body`, no longer a
  // descendant of `wrapper` - `wrapper.find(...)` would never find it again,
  // silently breaking repositioning for an already-open menu. Reuse the
  // stored reference in that case; only fall back to a fresh lookup for the
  // very first float.
  const existingMenu = el.data('floatingMenu');
  const menu = (existingMenu && existingMenu.length) ? existingMenu : wrapper.find('.menu-container').first();
  if (wrapper.length === 0 || tableContainer.length === 0 || menu.length === 0) { return; }

  if (!menu.data('floatOriginalParent')) {
    menu.data('floatOriginalParent', menu.parent());
    menu.data('floatOriginalNext', menu.next());
    menu.data('floatInput', el);
    el.data('floatingMenu', menu);
  }

  const rect = wrapper[0].getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUpward = spaceBelow < MENU_PREFERRED_HEIGHT && rect.top > spaceBelow;

  const computed = window.getComputedStyle(menu[0]);
  const overflowY = computed.overflowY;
  const minWidth = computed.minWidth;

  const effectiveWidth = Math.max(rect.width, parseFloat(minWidth) || 0);
  const left = window.scrollX + Math.max(0, Math.min(rect.left, window.innerWidth - effectiveWidth));

  const availableSpace = (openUpward ? rect.top : spaceBelow) - 8;
  const maxHeight = Math.min(MENU_PREFERRED_HEIGHT, Math.max(0, availableSpace));

  menu.addClass('tt-menu-floating').appendTo('body').css({
    left: left + 'px',
    width: rect.width + 'px',
    minWidth,
    overflowY,
    maxHeight: maxHeight + 'px',
    bottom: '',
  });

  // Anchored via `top` in document coordinates (not viewport-relative
  // `rect` values directly) so the menu scrolls with the page instead of
  // staying glued to the viewport (ET-3185 regression). Opening upward
  // needs the menu's actual rendered height (<=maxHeight, since content may
  // be shorter) to align its bottom edge with the wrapper's top - reading
  // offsetHeight here forces the layout needed to know it.
  const top = openUpward
    ? (window.scrollY + rect.top - menu[0].offsetHeight)
    : (window.scrollY + rect.bottom);
  menu.css('top', top + 'px');
}

function unfloatTypeaheadMenu(el) {
  const menu = el.data('floatingMenu');
  if (!menu || menu.length === 0 || !menu.hasClass('tt-menu-floating')) { return; }

  const originalNext = menu.data('floatOriginalNext');
  const originalParent = menu.data('floatOriginalParent');
  if (originalNext && originalNext.length) {
    menu.insertBefore(originalNext);
  } else if (originalParent && originalParent.length) {
    menu.appendTo(originalParent);
  }
  menu.removeClass('tt-menu-floating').removeData('floatOriginalParent floatOriginalNext floatInput').css({
    left: '', width: '', minWidth: '', overflowY: '', maxHeight: '', top: '', bottom: '',
  });
  el.removeData('floatingMenu');
}

// ET-3185: a floated menu lives in `document.body`, detached from its
// `.twitter-typeahead` wrapper. Destroying (`.typeahead('destroy')`) or
// replacing (Turbo Stream `replaceWith`) an input's ancestor only touches
// that ancestor's own subtree, so a menu floated out to body is never
// cleaned up by those operations and is orphaned permanently. Call this
// (scoped to whatever DOM is about to be torn down) beforehand so any
// affected menu closes - and unfloats back into the DOM about to be
// removed - first. Exported for GridNav.js/TurboCustomActions.js.
export function closeFloatingTypeaheadsWithin(root) {
  $('.tt-menu-floating').each(function() {
    const el = $(this).data('floatInput');
    if (el && el.length && $.contains(root, el[0]) && typeof el.typeahead === 'function') {
      el.typeahead('close');
    }
  });
}

function getManualListOptions(el) {
  const sourceEl = resolveManualTypeahead(el);
  if (!sourceEl || sourceEl.length === 0) { return []; }
  let cached = sourceEl.data('listOptionsCache');
  if (cached) { return cached; }
  let parsed = [];
  try {
    const raw = sourceEl.attr('data-list-options') || '[]';
    const value = JSON.parse(raw);
    parsed = Array.isArray(value) ? value : [];
  } catch (error) {
    parsed = [];
  }
  sourceEl.data('listOptionsCache', parsed);
  return parsed;
}

function enforceValidManualSelection(el) {
  const sourceEl = resolveManualTypeahead(el);
  if (!sourceEl || sourceEl.length === 0) { return true; }

  const answerField = sourceEl.closest('.input-group').find('.answer-text').first();
  const canonicalField = answerField.length ? answerField : sourceEl;

  const storedValue = (canonicalField.val() || '').toString().trim();
  let displayedValue = '';
  if (typeof el.typeahead === 'function') {
    displayedValue = (el.typeahead('val') || '').toString().trim();
  } else if (typeof sourceEl.typeahead === 'function') {
    displayedValue = (sourceEl.typeahead('val') || '').toString().trim();
  }
  const rawValue = displayedValue.length ? displayedValue : storedValue;

  if (!rawValue.length) {
    sourceEl.removeData('lastInvalidManualValue');
    return true;
  }

  const allowed = getManualListOptions(sourceEl)
    .map((opt) => (opt || '').toString().trim().toLowerCase())
    .filter((opt) => opt.length > 0);

  if (allowed.length === 0 || allowed.includes(rawValue.toLowerCase())) {
    sourceEl.removeData('lastInvalidManualValue');
    return true;
  }

  const previousInvalid = sourceEl.data('lastInvalidManualValue');
  if (previousInvalid !== rawValue) {
    ToastCustom({ title: 'Invalid response', message: 'Please choose one of the available options.' });
    sourceEl.data('lastInvalidManualValue', rawValue);
  }

  if (canonicalField.is('.answer-text')) {
    canonicalField.data('skipValidation', true).val('').trigger('change');
    CheckValidationsOnInput(canonicalField, '');
  } else {
    canonicalField.val('');
  }

  if (typeof el.typeahead === 'function') {
    el.typeahead('val', '');
    clearTypeaheadStateClasses(el);
  } else if (typeof sourceEl.typeahead === 'function') {
    sourceEl.typeahead('val', '');
    clearTypeaheadStateClasses(sourceEl);
  } else {
    canonicalField.val('');
    clearTypeaheadStateClasses(canonicalField);
  }
  return false;
}

export default function(parentContainer) {

  parentContainer.find('.manual-list-typeahead')
    .off('typeahead:select.typeahead typeahead:autocomplete.typeahead typeahead:close.typeahead typeahead:change.typeahead typeahead:open.typeahead typeahead:render.typeahead')
    .on('typeahead:open.typeahead', function() {
      const el = $(this);
      floatTypeaheadMenu(el);
      // Reparenting via appendTo() doesn't reset scroll position, so a menu
      // left scrolled from an earlier interaction can reopen showing options
      // partway down the list instead of from the top (ET-3185).
      const menu = el.data('floatingMenu');
      if (menu) { menu.scrollTop(0); }
    })
    .on('typeahead:render.typeahead', function() {
      // Only reposition an already-floating menu (mid-open, content changed).
      // A stray `render` can fire after close (e.g. enforceValidManualSelection
      // clearing the value 300ms later) - initiating a float from that would
      // strand the menu in a closed state with nothing left to unfloat it (ET-3185).
      const el = $(this);
      // A floated menu is no longer a descendant of `.twitter-typeahead`, so
      // it must be looked up via the stored reference, not `.find(...)`.
      const menu = el.data('floatingMenu');
      if (menu && menu.length && menu.hasClass('tt-menu-floating')) {
        floatTypeaheadMenu(el);
      }
    })
    .on('typeahead:select.typeahead typeahead:autocomplete.typeahead', function(ev, suggestion) {
      let el = $(this);
      let ig = el.closest('.input-group');
      let cell = el.closest('.survey-table-cell');
      let metricDir = $(this).attr('data-metric-direction');
      if (typeof metricDir === 'undefined') {metricDir = '';}
      const answerField = ig.find('.answer-text').first();
      const normalizedSuggestion = (typeof suggestion === 'string')
        ? suggestion
        : (suggestion && (suggestion.value || suggestion.label)) || '';
      const fallbackValue = answerField.length ? (answerField.val() || '').toString() : '';
      const finalValue = normalizedSuggestion.length > 0 ? normalizedSuggestion : fallbackValue;

      if (answerField.length && answerField[0] !== el[0]) {
        answerField.val(finalValue);
      }

      ToggleNewData(el);

      // Set class
      clearTypeaheadStateClasses(el);
      if ((suggestion === "Yes") && (metricDir !== '')) {
        el.addClass('yes-' + metricDir);
      } else if ((suggestion === "No") && (metricDir !== '')) {
        el.addClass('no-' + metricDir);
      } else if ((suggestion === "High") && (metricDir !== '')) {
        el.addClass('high-' + metricDir);
      } else if ((suggestion === "1: High Risk")) {
        el.addClass('low-desc');
      } else if ((suggestion === "Red")) {
        el.addClass('low-desc');
      } else if ((suggestion === "Low") && (metricDir !== '')) {
        el.addClass('low-' + metricDir);
      } else if ((suggestion === "3: Low Risk")) {
        el.addClass('medium');
      } else if ((suggestion === "Medium") && (metricDir !== '')) {
        el.addClass('medium');
      } else if ((suggestion === "2: Medium Risk")) {
        el.addClass('medium');
      } else if ((suggestion === "Yellow")) {
        el.addClass('medium');
      } else if ((suggestion === "4: Neutral")) {
        el.addClass('medium');
      } else if ((suggestion === "5: Positive Impact")) {
        el.addClass('high-desc');
      } else if ((suggestion === "Green")) {
        el.addClass('high-desc');
      } else if (suggestion === "Information not available") {
        el.addClass('information-not-available');
      } else if (suggestion.length > 0) {
        el.addClass('filled');
      } else if ((typeof suggestion !== 'undefined') && (suggestion.length > 0)) {
        el.addClass('filled');
      }
      if (typeof cell !== 'undefined') {
        cell.addClass('completed');
      }
      el.closest('.question-row').removeClass('incomplete');

      // Check validations based on the canonical answer field
      CheckValidationsOnInput(answerField.length ? answerField : el, finalValue);

      let valsChanged = $('.survey-card-body[data-val-changed=true]');
      let hasDependencies = ig.attr('data-has-dependencies');
      if ((valsChanged.length > 0) && (hasDependencies === "true")) {
        UpdateConditionals(ig, suggestion);
      }

      if (cell.length !== 0 && cell.data('hasTotals')) {
        UpdateTotals(cell);
      }

      // Update progress bar with new completion status
      UpdateProgressBar(this);

    })
    .on('typeahead:close.typeahead', function() {
      const el = $(this);
      unfloatTypeaheadMenu(el);
      setTimeout(function() {
        enforceValidManualSelection(el);
      }, 300);
      const ig = el.closest('.input-group');
      const answerField = el.closest('.input-group').find('.answer-text').first();
      const currentValue = (answerField.val() || '').toString().trim();

      const valsChanged = $('.survey-card-body[data-val-changed=true]');
      const hasDependencies = ig.attr('data-has-dependencies');
      if ((valsChanged.length > 0) && (hasDependencies === 'true')) {
        UpdateConditionals(ig, currentValue);
      }

      if (currentValue.trim().length === 0) {
        CheckValidationsOnInput(answerField.length ? answerField : el, '');
      }
      // Update progress bar when typeahead closes (handles manual clearing)
      UpdateProgressBar(answerField.length ? answerField[0] : this);
    })
    .on('blur.validateManualOptions', function() {
      const el = $(this);
      setTimeout(function() {
        enforceValidManualSelection(el);
      }, 300);
    });

  // The menu is positioned in document coordinates (see floatTypeaheadMenu)
  // so it scrolls naturally with the page, but .table-container's own
  // internal `overflow: auto` scroll doesn't move the document - so a menu
  // floated out of it would visually detach from its cell on that scroll.
  // Close rather than track continuous reposition for that case. Scoped to
  // whatever scrolled so scrolling one survey table doesn't close an
  // unrelated open dropdown in another table on the page.
  parentContainer.find('.table-container')
    .off('scroll.floatTypeahead')
    .on('scroll.floatTypeahead', function() {
      closeFloatingTypeaheadsWithin(this);
    });

  // Resize affects every table's layout on the page, so close all of them.
  $(window).off('resize.floatTypeahead').on('resize.floatTypeahead', function() {
    closeFloatingTypeaheadsWithin(document.body);
  });

}
